import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ArrowLeft, ArrowRight, Loader2, ExternalLink, Calendar, CheckCircle2, AlertCircle, Clock, Search, X } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

export default function History() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      });
      if (search.trim()) params.set("search", search.trim());
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  const getOverallScore = (job) => {
    const results = job.results || [];
    const scored = results.filter((r) => r.final_score != null && r.final_score > 0);
    if (scored.length === 0) return null;
    const avg = scored.reduce((sum, r) => sum + r.final_score, 0) / scored.length;
    return Math.round(avg);
  };

  const getScoreColor = (score) => {
    if (score >= 81) return { bg: "#10b981", text: "white" };
    if (score >= 61) return { bg: "#005EB8", text: "white" };
    if (score >= 41) return { bg: "#f59e0b", text: "white" };
    if (score >= 21) return { bg: "#ea580c", text: "white" };
    return { bg: "#ef4444", text: "white" };
  };

  const getStatusBadge = (job) => {
    if (job.status === "completed") {
      return <Badge className="bg-[#10b981] text-white">Completed</Badge>;
    }
    if (job.status === "processing") {
      return <Badge className="bg-brand text-white">Processing</Badge>;
    }
    if (job.status === "error") {
      return <Badge className="bg-[#ef4444] text-white">Failed</Badge>;
    }
    return <Badge variant="secondary">Queued</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-brand hover:text-brand-dark transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-brand-ink" data-testid="history-title">Analysis History</h1>
          <p className="text-[#6b7280] mt-2">View and search all of your past Revenue Acceleration Score analyses</p>
        </div>

        {/* Search */}
        <div className="mb-6 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by URL or company name…"
              className="pl-9 pr-9"
              data-testid="history-search"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#374151]"
                data-testid="history-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {!loading && (
            <span className="text-sm text-[#6b7280] whitespace-nowrap" data-testid="history-result-count">
              {total === 0
                ? "No results"
                : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
            </span>
          )}
        </div>

        {loading ? (
          <Card className="card p-12 text-center">
            <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
            <p className="text-[#6b7280]">Loading history...</p>
          </Card>
        ) : jobs.length === 0 ? (
          <Card className="card p-12 text-center">
            <Clock className="w-12 h-12 text-[#9ca3af] mx-auto mb-4" />
            {search.trim() ? (
              <>
                <h2 className="text-lg font-semibold text-[#374151] mb-2">No matching analyses</h2>
                <p className="text-[#6b7280] mb-6">No analyses match “{search.trim()}”. Try a different URL or company name.</p>
                <Button variant="outline" onClick={() => setSearchInput("")} data-testid="clear-search-empty">
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-[#374151] mb-2">No analyses yet</h2>
                <p className="text-[#6b7280] mb-6">Start your first Revenue Acceleration Score analysis to see results here.</p>
                <Link to="/">
                  <Button className="btn-primary" data-testid="start-first-analysis">
                    Start First Analysis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </>
            )}
          </Card>
        ) : (
          <Card className="card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>URLs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Results</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} data-testid={`history-row-${job.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#6b7280]" />
                        <span className="text-sm text-[#374151]">{formatDate(job.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        {job.urls.slice(0, 2).map((url, index) => (
                          <div key={index} className="text-sm text-[#374151] truncate">
                            {url}
                          </div>
                        ))}
                        {job.urls.length > 2 && (
                          <span className="text-xs text-[#6b7280]">+{job.urls.length - 2} more</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(job)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium text-[#374151]">
                          {job.completed} / {job.total}
                        </span>
                        {job.status === "error" && (
                          <AlertCircle className="w-4 h-4 text-[#ef4444]" />
                        )}
                        {job.completed === job.total && job.total > 0 && job.status !== "error" && (
                          <CheckCircle2 className="w-4 h-4 text-[#10b981]" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const score = getOverallScore(job);
                        if (score === null) {
                          return <span className="text-sm text-[#9ca3af]">—</span>;
                        }
                        const { bg, text } = getScoreColor(score);
                        return (
                          <span
                            className="inline-flex items-center justify-center rounded-full text-sm font-semibold"
                            style={{
                              backgroundColor: bg,
                              color: text,
                              minWidth: "2.5rem",
                              padding: "0.2rem 0.6rem",
                            }}
                            data-testid={`score-badge-${job.id}`}
                          >
                            {score}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          data-testid={`view-job-${job.id}`}
                        >
                          View Job
                        </Button>
                        {job.results && job.results.length > 0 && job.results[0].deliverable_id && (
                          <Button
                            className="btn-primary"
                            size="sm"
                            onClick={() => navigate(`/results/${job.results[0].deliverable_id}`)}
                            data-testid={`view-results-${job.id}`}
                          >
                            Results
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                        {job.results && job.results.length > 0 && !job.results[0].deliverable_id && job.results[0].public_score_id && (
                          <Button
                            className="btn-primary"
                            size="sm"
                            onClick={() => navigate(`/public-scores/${job.results[0].public_score_id}`)}
                            data-testid={`view-public-score-${job.id}`}
                          >
                            View Score
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between" data-testid="history-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="history-prev-page"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-[#6b7280]">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              data-testid="history-next-page"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
